"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { content } from "@/config/content";
import { useCart } from "@/components/shared/CartProvider";
import { useSiteContent } from "@/lib/useSiteContent";
import { useSiteSettings } from "@/lib/useSiteSettings";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
import { useFocusTrap } from "@/lib/useFocusTrap";
import MemberButton from "./MemberButton";
import { MEMBERS_ENABLED } from "@/lib/featureFlags";

function HamburgerIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-text)"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line x1="3" y1="7" x2="21" y2="7" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="17" x2="21" y2="17" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-text)"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function CartButton() {
  const { count, openDrawer } = useCart();
  return (
    <button
      onClick={openDrawer}
      className="relative w-11 h-11 flex items-center justify-center"
      aria-label={`購物車 ${count} 件`}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--color-text)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="9" cy="21" r="1" />
        <circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
      </svg>
      {count > 0 && (
        <span
          className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold text-white leading-none px-1"
          style={{
            backgroundColor: "var(--color-accent)",
            borderRadius: "9px",
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const [prevPathname, setPrevPathname] = useState(pathname);
  const overlayRef = useRef(null);
  if (prevPathname !== pathname) {
    setPrevPathname(pathname);
    setOpen(false);
  }
  const handleClose = useCallback(() => setOpen(false), []);
  useBodyScrollLock(open);
  useFocusTrap({
    isActive: open,
    containerRef: overlayRef,
    onEscape: handleClose,
  });

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // DB-driven nav items; fall back to config/content.js when DB empty
  const { items: navRows } = useSiteContent("nav_item");
  const navItems = navRows.length
    ? navRows.map((row) => ({ label: row.title || "", href: row.body || "#" }))
    : content.nav || [];

  // DB-driven brand display; fall back to config when DB empty
  const { settings: brand } = useSiteSettings("brand");
  const brandName = brand.brand_name || content.brand.name;
  const brandIgUrl = brand.brand_ig_url || content.brand.igUrl;

  if (pathname.startsWith("/admin")) return null;

  // 貼紙頁有自己的 sticky 結帳 bar，隱藏認領圖購物車避免混淆
  const hideClaimCart = pathname.startsWith("/stickers");

  return (
    <>
      <nav
        className="sticky top-0 z-50"
        style={{
          backgroundColor: "var(--color-bg)",
          borderBottom: scrolled
            ? "1px solid transparent"
            : "1px solid var(--color-cardBorder)",
          boxShadow: scrolled ? "0 2px 16px rgba(0,0,0,0.12)" : "none",
          height: scrolled
            ? "calc(48px + env(safe-area-inset-top, 0px))"
            : "var(--navbar-h)",
          transition:
            "box-shadow 0.25s ease, border-color 0.25s ease, height 0.25s ease",
        }}
      >
        <div className="flex items-center justify-between px-4 md:px-5 h-full max-w-6xl mx-auto">
          <Link
            href="/"
            className="font-bold tracking-wider shrink-0"
            style={{
              fontFamily: "var(--font-heading)",
              color: "var(--color-text)",
              fontSize: scrolled ? "15px" : "18px",
              transition: "font-size 0.25s ease",
            }}
          >
            {brandName}
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            {navItems.map((item) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className="relative text-base tracking-wide transition-opacity hover:opacity-100 py-1"
                  style={{
                    fontFamily: "var(--font-body)",
                    color: "var(--color-text)",
                    opacity: isActive ? 1 : 0.55,
                    fontWeight: isActive ? 600 : 400,
                    borderBottom: isActive
                      ? "2px solid var(--color-accent)"
                      : "2px solid transparent",
                    transition: "opacity 0.2s ease, border-color 0.2s ease",
                  }}
                >
                  {item.label}
                </Link>
              );
            })}
            <a
              href={brandIgUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-base tracking-wide"
              style={{ color: "var(--color-textMuted)" }}
            >
              IG
            </a>
            {!hideClaimCart && <CartButton />}
            {MEMBERS_ENABLED && <MemberButton />}
          </div>

          {/* Mobile: cart + hamburger */}
          <div className="flex items-center md:hidden">
            {!hideClaimCart && <CartButton />}
            {MEMBERS_ENABLED && <MemberButton />}
            <button
              onClick={() => setOpen((p) => !p)}
              className="w-11 h-11 flex items-center justify-center"
              aria-label={open ? "關閉選單" : "開啟選單"}
            >
              <HamburgerIcon />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile fullscreen overlay */}
      {open && (
        <div
          ref={overlayRef}
          role="dialog"
          aria-modal="true"
          aria-label="主選單"
          className="fixed inset-0 z-[60] flex flex-col md:hidden"
          style={{
            backgroundColor: "var(--color-bg)",
            overscrollBehavior: "contain",
          }}
        >
          {/* Top bar */}
          <div
            className="flex items-center justify-between px-4 shrink-0"
            style={{
              height: "var(--navbar-h)",
              borderBottom: "1px solid var(--color-cardBorder)",
            }}
          >
            <Link
              href="/"
              onClick={() => setOpen(false)}
              className="text-sm font-bold tracking-wider"
              style={{
                fontFamily: "var(--font-heading)",
                color: "var(--color-text)",
              }}
            >
              {brandName}
            </Link>
            <button
              onClick={() => setOpen(false)}
              className="w-11 h-11 flex items-center justify-center"
              aria-label="關閉選單"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Menu items (scrollable when content overflows) */}
          <div
            className="flex-1 overflow-y-auto"
            style={{
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div className="min-h-full flex flex-col items-center justify-center gap-7 py-8 px-4">
              {navItems.map((item) => {
                const isActive = pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="text-base tracking-widest min-h-11 flex items-center uppercase"
                    style={{
                      fontFamily: "var(--font-heading)",
                      color: "var(--color-text)",
                      opacity: isActive ? 1 : 0.4,
                      fontWeight: isActive ? 700 : 400,
                      letterSpacing: "0.1em",
                    }}
                  >
                    {item.label}
                  </Link>
                );
              })}

              <div
                className="w-8 my-1"
                style={{ borderTop: "1px solid var(--color-cardBorder)" }}
              />

              {/* Mobile: member menu entry in the fullscreen overlay */}
              {MEMBERS_ENABLED && (
                <MobileMemberLinks onClose={() => setOpen(false)} />
              )}

              <a
                href={brandIgUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-2.5 text-xs font-medium tracking-widest min-h-11"
                style={{
                  color: "#fff",
                  background:
                    "linear-gradient(135deg, #833AB4, #E1306C, #F77737)",
                  borderRadius: "var(--radius-full)",
                  letterSpacing: "0.1em",
                }}
              >
                <InstagramIcon />
                INSTAGRAM
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MobileMemberLinks({ onClose }) {
  return (
    <div className="flex flex-col items-center gap-3 mt-2 w-full px-10">
      <Link
        href="/member"
        onClick={onClose}
        className="w-full max-w-xs py-3 text-center text-sm font-medium"
        style={{
          backgroundColor: "var(--color-accent)",
          color: "#fff",
          borderRadius: "var(--radius-full)",
          letterSpacing: "0.05em",
        }}
      >
        會員中心
      </Link>
    </div>
  );
}
